import { LeftSidebarDirectory } from "../../types";
import { DIRECTORY_HEADER_HEIGHT, MIN_FLEX_GROW } from "./constants";
import { DirectoryState } from "./types";

/**
 * INVERSE FUNCTION: Calculate flex-grow values from target pixel heights.
 *
 * This is the mathematical inverse of calculateDirectoryHeights().
 * Given desired heights, it calculates what flex-grow values would produce those heights.
 *
 * @param targetHeights - Desired height in pixels for each directory
 * @param currentStates - Current directory states (to preserve open/closed status)
 * @param containerHeight - Total height of the container in pixels
 * @returns New directory states with calculated flex-grow values
 */
export function calculateFlexGrowFromHeights(
  targetHeights: Record<LeftSidebarDirectory, number>,
  currentStates: Record<LeftSidebarDirectory, DirectoryState>,
  containerHeight: number
): Record<LeftSidebarDirectory, DirectoryState> {
  // Calculate space available for open directories
  const collapsedCount = Object.values(currentStates).filter(
    s => !s.isOpen
  ).length;
  const availableSpace =
    containerHeight - collapsedCount * DIRECTORY_HEADER_HEIGHT;

  if (availableSpace <= 0) {
    // No space for open directories - shouldn't happen but handle gracefully
    return currentStates;
  }

  // Count open directories - normalize flex-grow to sum to this count
  // Note: openCount is guaranteed > 0 here (if all closed, availableSpace would be <= 0)
  const openCount = Object.values(currentStates).filter(s => s.isOpen).length;
  const totalFlexGrow = openCount;

  // Convert heights to flex-grow values
  const result: Record<string, DirectoryState> = {};

  Object.entries(currentStates).forEach(([key, state]) => {
    const dir = key as LeftSidebarDirectory;

    if (!state.isOpen) {
      // Collapsed directories always have flexGrow: 0
      result[key] = { isOpen: false, flexGrow: 0 };
    } else {
      // Open directory: flexGrow = (targetHeight / availableSpace) × totalFlexGrow
      const targetHeight = targetHeights[dir];
      const newFlexGrow = (targetHeight / availableSpace) * totalFlexGrow;

      result[key] = {
        isOpen: true,
        flexGrow: Math.max(MIN_FLEX_GROW, newFlexGrow),
      };
    }
  });

  return result;
}
